import { unstable_cache as serverCache } from 'next/cache';
import { featureFlipping, Locale } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import {
    CommonDI,
    ProductsRepositoryType,
    PriceLegalMentionsRepositoryType,
    LoyaltyConfigsRepositoryType,
} from '../../../../DI';
import { PaymentMethodsValue, ProductDetailValue, VipBrandsValue } from '../../../../server';
import { overwriteProductMedia } from './overwriteProductMedia';

const PRODUCT_INFO_PROVIDER_REVALIDATION_TIME_SECONDS = 10 * 60; // 10 minutes
const VARIANT_URL_REPLACE_REGEX = /-P?[0-9]+/;

/**
 * Fetches product detail information with cache capabilities.
 *
 * @param {Locale} locale - The locale for the request.
 * @param {string} productId - The ID of the product to fetch.
 * @param {VipBrandsValue} vipBrands - VIP brands information.
 * @param {PaymentMethodsValue} paymentMethods - Payment methods information.
 * @returns {Promise<ProductDetailValue | undefined>} The detailed product information or undefined if not found.
 */
export const getProductDetailInfo = serverCache(
    async (
        locale: Locale,
        productId: string,
        vipBrands: VipBrandsValue,
        paymentMethods: PaymentMethodsValue,
    ): Promise<ProductDetailValue | undefined> => {
        const loyaltyConfigResult = await (featureFlipping.isLoyaltyConfigEnabled
            ? CommonDI.get(LoyaltyConfigsRepositoryType).fetchLoyaltyConfiguration(locale)
            : Promise.resolve(undefined));

        const loyaltyConfig = unwrapResultSuccess(loyaltyConfigResult);

        // Call multiple API datasource if reconciliation feature flags are activated
        const [productResult, productsListResult] = await Promise.all([
            CommonDI.get(ProductsRepositoryType).getProductById(
                locale,
                productId,
                vipBrands,
                paymentMethods,
                true,
                false,
                loyaltyConfig,
            ),
            featureFlipping.isPdpProductListReconciliationEnabled
                ? CommonDI.get(ProductsRepositoryType).getProducts(locale, [productId], vipBrands)
                : undefined,
            CommonDI.get(PriceLegalMentionsRepositoryType).fetchEnablePriceLegalMentions(locale),
        ]);

        if (productResult.type === 'empty' || productResult.type === 'failure') {
            return undefined;
        }

        // Products list reconciliation, will be opt-out when SODA API data are available
        if (featureFlipping.isPdpProductListReconciliationEnabled && productsListResult) {
            const unwrappedProductsList = unwrapResultSuccess(productsListResult)?.[0];
            if (unwrappedProductsList) {
                if (unwrappedProductsList.isMasterProduct) {
                    productResult.data.sku = unwrappedProductsList.defaultVariantId;
                }
                productResult.data.url = unwrappedProductsList.href;

                productResult.data.variants.forEach((variant) => {
                    variant.url = unwrappedProductsList.href.replace(VARIANT_URL_REPLACE_REGEX, `-${variant.id}`);
                });
            }
        }

        // Product media reconciliation, will be opt-out when SODA API data are available
        if (featureFlipping.isPdpMediaReconciliationEnabled) {
            await overwriteProductMedia(locale, productResult.data, vipBrands);
        }

        return productResult.data;
    },
    undefined,
    { revalidate: PRODUCT_INFO_PROVIDER_REVALIDATION_TIME_SECONDS },
);
